// client/src/services/supabaseTournamentService.js
import { supabase } from './supabase';

const supabaseTournamentService = {
  async createTournament(name, structure, timerState, details) {
    const { data, error } = await supabase.functions.invoke('create-tournament', {
      body: { name, structure, timerState, details },
    });

    if (error) {
      return { data: null, error: error };
    }
    if (data.success) {
      return { data: data.tournament, error: null };
    } else {
      return { data: null, error: data.error || new Error('Unknown error from Edge Function') };
    }
  },

  async getTournamentsForUser(userId) {
    const { data, error } = await supabase
      .from('tournaments')
      .select('id, name, owner_id, created_at, updated_at, timer_state, structure')
      .eq('owner_id', userId);

    if (error) {
      return { data: null, error };
    }
    return { data: data, error: null };
  },

  async getTournamentById(tournamentId) {
    const { data, error } = await supabase
      .from('tournaments')
      .select('id, name, structure, timer_state, details, owner_id')
      .eq('id', tournamentId)
      .single();

    if (error) {
      return { data: null, error };
    }

    return { data: data, error: null };
  },

  async updateTournamentTimerState(tournamentId, timerState, remainingTime, rowIndex, playStartTime = null) {
    const { data, error } = await supabase
      .from('tournaments')
      .update({
        timer_state: {
          state: timerState,
          remaining: remainingTime,
          rowIndex: rowIndex,
          playStartTime: playStartTime,
        }
      })
      .eq('id', tournamentId);

    if (error) {
      return { data: null, error };
    }
    return { data: { success: true, data }, error: null };
  },

  async updateTournamentStructure(tournamentId, name, structure, details, timerState) {
    // まず現在のtimer_stateを取得
    const { data: currentTournament, error: fetchError } = await supabase
      .from('tournaments')
      .select('timer_state')
      .eq('id', tournamentId)
      .single();

    if (fetchError) {
      return { data: null, error: fetchError };
    }

    const existingTimerState = currentTournament?.timer_state || {};

    const updatePayload = {
      structure: structure,
    };

    if (name !== undefined) {
      updatePayload.name = name;
    }
    updatePayload.details = details;

    if (timerState) {
      // 既存のtimer_stateと新しいtimerStateをマージ
      updatePayload.timer_state = {
        ...existingTimerState, // 既存のプロパティを保持
        remaining: timerState.remaining,
        value: timerState.value,
        // 必要に応じて、timerStateから他のプロパティもマージ
        // 例: state: timerState.state,
      };
    } else {
      // timerStateが渡されない場合でも、既存のtimer_stateを保持
      updatePayload.timer_state = existingTimerState;
    }

    const { data, error } = await supabase
      .from('tournaments')
      .update(updatePayload)
      .eq('id', tournamentId);

    if (error) {
      return { data: null, error };
    }
    return { data: { success: true, data }, error: null };
  },

  async updateTournamentDetails(tournamentId, details) {
    const { data, error } = await supabase
      .from('tournaments')
      .update({ details })
      .eq('id', tournamentId);

    if (error) {
      return { data: null, error };
    }
    return { data: { success: true, data }, error: null };
  },

  async deleteTournament(tournamentId) {
    const { data, error } = await supabase.functions.invoke('delete-tournament', {
      body: { tournamentId },
    });

    if (error) {
      return { data: null, error: error };
    }
    if (data.success) {
      return { data: { success: true }, error: null };
    } else {
      return { data: null, error: data.error || new Error('Unknown error from Edge Function') };
    }
  },

  async getAllTournaments(limit, offset) {
    const { data, error } = await supabase.functions.invoke('get-all-tournaments', {
      body: { limit, offset },
    });

    if (error) {
      return { data: null, error };
    }
    return { data: { tournaments: data.tournaments, totalCount: data.totalCount }, error: null };
  },

  async adminDeleteTournament(tournamentId) {
    const { data, error } = await supabase.functions.invoke('admin-delete-tournament', {
      body: { tournamentId },
    });

    if (error) {
      return { data: null, error: error };
    }
    if (data.success) {
      return { data: { success: true }, error: null };
    } else {
      return { data: null, error: data.error || new Error('Unknown error from Edge Function') };
    }
  },

  async getUserTier(userId) {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('tier')
      .eq('id', userId)
      .single();

    if (error) {
      return { data: null, error };
    }
    return { data: { tier: data.tier }, error: null };
  },

  async updateTournamentState(tournamentId, state) {
    const payload = {
      tournament_id: tournamentId,
    };
    if (state.name !== undefined) payload.name = state.name;
    if (state.structure !== undefined) payload.structure = state.structure;
    if (state.details !== undefined) payload.details = state.details;
    if (state.timer_state !== undefined) payload.timer_state = state.timer_state;

    // Check for network offline before attempting request
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return { data: null, error: { message: 'Network connection lost (Offline)' } };
    }

    // Timeout helper
    const timeout = (ms) => new Promise((_, reject) => setTimeout(() => reject(new Error('Request timed out')), ms));

    let error;
    try {
      // Race the upsert against a 3 second timeout
      const result = await Promise.race([
        supabase.from('tournament_states').upsert(payload),
        timeout(3000)
      ]);
      error = result.error;
    } catch (e) {
      error = e;
    }

    if (error) {
      console.error('Error updating tournament state:', error);
      return { data: null, error };
    }

    // Also update the persistent 'tournaments' table so data isn't lost on reload
    // We map keys back to the tournaments table schema (which matches payload mostly)
    const persistentPayload = {};
    if (payload.name !== undefined) persistentPayload.name = payload.name;
    if (payload.structure !== undefined) persistentPayload.structure = payload.structure;
    if (payload.details !== undefined) persistentPayload.details = payload.details;
    if (payload.timer_state !== undefined) persistentPayload.timer_state = payload.timer_state;

    // Fire and forget the persistent update to avoid blocking UI? 
    // Or await it to ensure safety? Better to await for data integrity.
    if (Object.keys(persistentPayload).length > 0) {
      const { error: persistError } = await supabase
        .from('tournaments')
        .update(persistentPayload)
        .eq('id', tournamentId);

      if (persistError) {
        console.error('Error updating persistent tournament:', persistError);
        // We warn but don't fail the operation if hot sync succeeded? 
        // Or should we fail? Given reload issue, we should probably log it loud.
      }
    }

    return { data: { success: true }, error: null };
  },


  /**
   * 指定されたトーナメントの状態変更を購読します。
   * @param {string} tournamentId - 購読するトーナメントのID。
   * @param {function} callback - 状態変更時に呼び出されるコールバック関数。
   * @returns {object} Supabase Realtime Channel オブジェクト。
   */
  subscribeToTournamentState: (tournamentId, callback, onStatusChange) => {
    // チャンネル名の重複によるシングルトン（使い回し）化を防ぎ、確実な新規作成と破棄を保証するためのユニークID
    const uniqueId = Math.random().toString(36).substring(2, 10);
    const channel = supabase
      .channel(`tournament_state:${tournamentId}:${uniqueId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE イベントを購読
          schema: 'public',
          table: 'tournament_states',
          filter: `tournament_id=eq.${tournamentId}`,
        },
        (payload) => {
          // payload.new に最新のデータが含まれる
          callback(payload.new);
        }
      )
      .subscribe((status, err) => {
        if (onStatusChange) {
          onStatusChange(status, err);
        }
      });

    return channel;
  },

  /**
   * 指定されたトーナメントの状態変更の購読を解除します。
   * @param {object} channel - subscribeToTournamentState が返した Supabase Realtime Channel オブジェクト。
   */
  unsubscribeFromTournamentState: async function (channel) {
    if (channel) {
      await channel.unsubscribe();
      // チャンネルを削除してリソースを解放
      await supabase.removeChannel(channel);
    }
  },
};

export default supabaseTournamentService;
